import { workflowSchedulesApi } from '@/api/workflowSchedules'
import type {
  CreateWorkflowScheduleRequest,
  PaginationParams,
  UpdateWorkflowScheduleRequest,
  WorkflowScheduleListFilters,
  WorkflowScheduleOccurrenceFilters,
  WorkflowScheduleRecurrenceRequest,
} from '@/types'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useDebounce } from './useDebounce'
import { useTenantKey } from './useTenantKey'

const SCHEDULES = 'workflow-schedules'
const BACKGROUND_REFRESH_MS = 30_000
const PREVIEW_DEBOUNCE_MS = 400

function useInvalidateSchedules(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return () => {
    queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, SCHEDULES, 'list') })
    queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, SCHEDULES, 'detail') })
    queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, SCHEDULES, 'occurrences') })
  }
}

export function useWorkflowSchedules(projectId: string, filters: WorkflowScheduleListFilters, pagination: PaginationParams) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, SCHEDULES, 'list', { ...filters, ...pagination }),
    queryFn: () => workflowSchedulesApi.getAll(projectId, filters, pagination),
    enabled: !!projectId,
    placeholderData: keepPreviousData,
  })
}

export function useWorkflowSchedule(projectId: string, scheduleId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, SCHEDULES, 'detail', scheduleId),
    queryFn: () => workflowSchedulesApi.getById(projectId, scheduleId!),
    enabled: !!projectId && !!scheduleId,
    refetchInterval: BACKGROUND_REFRESH_MS,
  })
}

export function useWorkflowScheduleOccurrences(
  projectId: string,
  scheduleId: string | undefined,
  filters: WorkflowScheduleOccurrenceFilters,
  pagination: PaginationParams,
) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, SCHEDULES, 'occurrences', scheduleId, { ...filters, ...pagination }),
    queryFn: () => workflowSchedulesApi.getOccurrences(projectId, scheduleId!, filters, pagination),
    enabled: !!projectId && !!scheduleId,
    placeholderData: keepPreviousData,
    refetchInterval: BACKGROUND_REFRESH_MS,
  })
}

export function useWorkflowSchedulePreview(projectId: string, request: WorkflowScheduleRecurrenceRequest | null) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, SCHEDULES, 'preview', request),
    queryFn: () => workflowSchedulesApi.preview(projectId, request!),
    enabled: !!projectId && !!request,
    retry: false,
    staleTime: 0,
  })
}

export function useDebouncedWorkflowSchedulePreview(projectId: string, request: WorkflowScheduleRecurrenceRequest | null) {
  const requestKey = request ? JSON.stringify(request) : ''
  const debouncedKey = useDebounce(requestKey, PREVIEW_DEBOUNCE_MS)
  const debouncedRequest = useMemo<WorkflowScheduleRecurrenceRequest | null>(
    () => (debouncedKey ? JSON.parse(debouncedKey) as WorkflowScheduleRecurrenceRequest : null),
    [debouncedKey],
  )
  const { data, isFetching, isError, error } = useWorkflowSchedulePreview(projectId, debouncedRequest)
  return {
    data,
    isError,
    error,
    debouncedRequest,
    isSettling: requestKey !== debouncedKey || isFetching,
  }
}

export type DebouncedWorkflowSchedulePreview = ReturnType<typeof useDebouncedWorkflowSchedulePreview>

export function useCreateWorkflowSchedule(projectId: string) {
  const invalidate = useInvalidateSchedules(projectId)
  return useMutation({
    mutationFn: (data: CreateWorkflowScheduleRequest) => workflowSchedulesApi.create(projectId, data),
    onSuccess: invalidate,
  })
}

export function useUpdateWorkflowSchedule(projectId: string) {
  const invalidate = useInvalidateSchedules(projectId)
  return useMutation({
    mutationFn: ({ scheduleId, data }: { scheduleId: string; data: UpdateWorkflowScheduleRequest }) =>
      workflowSchedulesApi.update(projectId, scheduleId, data),
    onSuccess: invalidate,
  })
}

export function usePauseWorkflowSchedule(projectId: string) {
  const invalidate = useInvalidateSchedules(projectId)
  return useMutation({
    mutationFn: (scheduleId: string) => workflowSchedulesApi.pause(projectId, scheduleId),
    onSettled: invalidate,
  })
}

export function useResumeWorkflowSchedule(projectId: string) {
  const invalidate = useInvalidateSchedules(projectId)
  return useMutation({
    mutationFn: (scheduleId: string) => workflowSchedulesApi.resume(projectId, scheduleId),
    onSettled: invalidate,
  })
}

export function useDeleteWorkflowSchedule(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (scheduleId: string) => workflowSchedulesApi.delete(projectId, scheduleId),
    onSuccess: (_data, scheduleId) => {
      queryClient.removeQueries({ queryKey: tenantKey('projects', projectId, SCHEDULES, 'detail', scheduleId) })
      queryClient.removeQueries({ queryKey: tenantKey('projects', projectId, SCHEDULES, 'occurrences', scheduleId) })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, SCHEDULES, 'list') })
    },
  })
}
